import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import Chart from 'react-apexcharts';
import { Card, CardBody, Col, Container, Row, Table, Button, Spinner, Alert } from 'reactstrap';
import CountUp from 'react-countup';

const AdminDashboard = () => {
  const url = `${process.env.REACT_APP_BASE_URL}`;
  const [summary, setSummary] = useState(null);
  const [trends, setTrends] = useState([]);
  // breakdown chart removed per request
  const [pending, setPending] = useState({ data: [], pagination: {} });
  const [alerts, setAlerts] = useState({ data: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [apiError, setApiError] = useState(null);

  // safe defaults to avoid runtime errors when API fails or returns null
  const safeSummary = summary || {
    users: { total: 0, active: 0, blocked: 0 },
    employees: { total: 0, newLast7: 0, deleted: 0 },
    companies: { total: 0 },
    checkups: { today: 0, last7: 0, last30: 0, incomplete: 0 },
    loginStats: { totalAttempts: 0, locked: 0 },
    healthAlerts: { count: 0 },
  };

  const fetchAll = async () => {
    setRefreshing(true);

    // Get token explicitly (same pattern as other working pages)
    const token = localStorage.getItem("token");
    if (!token) {
      setApiError("Authentication required - please login");
      setLoading(false);
      setRefreshing(false);
      return;
    }

    // Build headers explicitly like other working API calls
    const config = {
      headers: { Authorization: `Bearer ${token}` },
    };

    const unwrap = (res) => {
      // Handle several possible axios/response shapes:
      // - axios interceptor unwrapped: res = { success, data }
      // - full axios response: res = { data: { success, data } }
      // - direct payload: res = { ...payload... }
      // - HTML/string (proxy misroute): res is a string containing '<!doctype html>'
      if (!res) return null;
      if (typeof res === 'string') return { __html: true, raw: res };
      const body = res.data ?? res;
      if (!body) return null;
      // If API wrapper { success: true, data: ... }
      if (body && (body.success !== undefined || body.data !== undefined)) {
        return body.data ?? body;
      }
      // Already a payload
      return body;
    };

    try {
      console.log('[AdminDashboard] Fetching dashboard data with token:', token.substring(0, 20) + '...');
      console.log('[AdminDashboard] API base URL:', url);
      
      const [sResRaw, tResRaw, pResRaw, aResRaw] = await Promise.all([
        axios.get(`${url}/api/dashboard/summary`, config),
        axios.get(`${url}/api/dashboard/checkup-trends?days=30`, config),
        axios.get(`${url}/api/dashboard/pending-reports?limit=8`, config),
        axios.get(`${url}/api/dashboard/health-alerts?limit=8`, config),
      ]);

      console.log('[AdminDashboard] Raw response types:', {
        summary: typeof sResRaw,
        summaryKeys: sResRaw ? Object.keys(sResRaw).slice(0, 5) : [],
        summarySnippet: JSON.stringify(sResRaw).substring(0, 200)
      });
      
      console.log('[AdminDashboard] Raw responses received:', {
        summary: sResRaw?.success || sResRaw?.data?.success,
        trends: tResRaw?.success || tResRaw?.data?.success,
      });

      const sPayload = unwrap(sResRaw);
      const tPayload = unwrap(tResRaw);
      const pPayload = unwrap(pResRaw);
      const aPayload = unwrap(aResRaw);

      // Detect server returning HTML (common proxy/misroute symptom)
      if (sPayload && sPayload.__html) {
        console.error('API returned HTML for /api/dashboard/summary — likely proxy/static-server misconfiguration');
        throw new Error('API returned HTML (check reverse-proxy / server configuration)');
      }

      setSummary(sPayload || null);
      setTrends(Array.isArray(tPayload) ? tPayload : (tPayload?.series || []) );
      setPending({ data: Array.isArray(pPayload) ? pPayload : (pPayload?.data || []), pagination: (pPayload?.pagination || {}) });
      setAlerts({ data: Array.isArray(aPayload) ? aPayload : (aPayload?.data || []), total: aPayload?.total || 0 });

      // clear previous API error (if any)
      setApiError(null);
    } catch (err) {
      console.error('Dashboard fetch error', err);
      // surface a short, actionable error message for the UI
      setApiError(err.message || 'Failed to fetch dashboard data');

      // ensure UI doesn't break if API fails
      setSummary(null);
      setTrends([]);
      setPending({ data: [], pagination: {} });
      setAlerts({ data: [], total: 0 });
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const trendSeries = useMemo(() => {
    const categories = trends.map((r) => r._id);
    const data = trends.map((r) => r.count);
    return { categories, data };
  }, [trends]);

  if (loading) return (
    <div className="d-flex justify-content-center align-items-center" style={{height: '60vh'}}>
      <Spinner color="primary" />
    </div>
  );

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="mb-3 align-items-center">
          <Col><h4 className="mb-0">Admin Dashboard</h4></Col>
          <Col className="text-end">
            <Button color="primary" onClick={fetchAll} disabled={refreshing}>
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </Col>
        </Row>

        {apiError && (
          <Row className="mb-3">
            <Col>
              <Alert color="danger" isOpen>
                <strong>API error:</strong> {apiError} — the API may be returning HTML (check reverse-proxy / server configuration).
              </Alert>
            </Col>
          </Row>
        )}

        {/* KPI row */}
        <Row>
          <Col md={3}>
            <Card>
              <CardBody>
                <h6 className="text-muted">Users</h6>
                <h3><CountUp end={safeSummary.users.total} /></h3>
                <div className="text-muted small">Active: {safeSummary.users.active} • Blocked: {safeSummary.users.blocked}</div>
              </CardBody>
            </Card>
          </Col>
          <Col md={3}>
            <Card>
              <CardBody>
                <h6 className="text-muted">Employees</h6>
                <h3><CountUp end={safeSummary.employees.total} /></h3>
                <div className="text-muted small">New (7d): {safeSummary.employees.newLast7} • Deleted: {safeSummary.employees.deleted}</div>
              </CardBody>
            </Card>
          </Col>
          <Col md={2}>
            <Card>
              <CardBody>
                <h6 className="text-muted">Companies</h6>
                <h3><CountUp end={safeSummary.companies.total} /></h3>
                <div className="text-muted small">Total companies</div>
              </CardBody>
            </Card>
          </Col>
          <Col md={2}>
            <Card>
              <CardBody>
                <h6 className="text-muted">Checkups (Today)</h6>
                <h3><CountUp end={safeSummary.checkups.today} /></h3>
                <div className="text-muted small">Last 7d: {safeSummary.checkups.last7} • 30d: {safeSummary.checkups.last30}</div>
              </CardBody>
            </Card>
          </Col>
          <Col md={2}>
            <Card>
              <CardBody>
                <h6 className="text-muted">Security</h6>
                <h3><CountUp end={safeSummary.loginStats.locked} /></h3>
                <div className="text-muted small">Locked accounts</div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Charts row */}
        <Row className="mt-3">
          <Col lg={12}>
            <Card>
              <CardBody>
                <h5 className="mb-3">Checkups — last 30 days</h5>
                <Chart
                  options={{
                    chart: { id: 'checkup-trend', toolbar: { show: false } },
                    xaxis: { categories: trendSeries.categories },
                    stroke: { curve: 'smooth' },
                    tooltip: { theme: 'dark' },
                  }}
                  series={[{ name: 'Checkups', data: trendSeries.data }]}
                  type="area"
                  height={300}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Tables row */}
        <Row className="mt-3">
          <Col lg={6}>
            <Card>
              <CardBody>
                <h5 className="mb-3">Pending reports</h5>
                <Table responsive hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Employee</th>
                      <th>Company</th>
                      <th>Date</th>
                      <th>Missing</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pending.data.length === 0 ? (
                      <tr><td colSpan={5} className="text-center">No pending reports</td></tr>
                    ) : pending.data.map((r, i) => (
                      <tr key={r._id}>
                        <td>{i + 1}</td>
                        <td>{r.employeeName || '—'}</td>
                        <td>{r.companyName || '—'}</td>
                        <td>{new Date(r.checkupDate).toLocaleDateString()}</td>
                        <td>{(r.missingForms || []).join(', ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>

          <Col lg={6}>
            <Card>
              <CardBody>
                <h5 className="mb-3">Health alerts</h5>
                <Table responsive hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Employee</th>
                      <th>Company</th>
                      <th>BMI</th>
                      <th>Pulse</th>
                    </tr>
                  </thead>
                  <tbody>
                    {alerts.data.length === 0 ? (
                      <tr><td colSpan={5} className="text-center">No alerts</td></tr>
                    ) : alerts.data.map((a, i) => (
                      <tr key={a._id}>
                        <td>{i + 1}</td>
                        <td>{a.employeeName || '—'}</td>
                        <td>{a.companyName || '—'}</td>
                        <td>{a.bmi || '—'}</td>
                        <td>{a.pulse || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>
        </Row>

      </Container>
    </div>
  );
};

export default AdminDashboard;
